// src/pages/item_delivery_logs/ItemDeliveryLogForm.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  FormHelperText,
  Select,
  MenuItem,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import itemDeliveryLogService from '../../services/itemDeliveryLogService';
import AdminHeader from '../../components/AdminHeader';
import LayoutContainer from '../../components/LayoutContainer';
import itemDeliveryService from '../../services/itemDeliveryService';
import { isEmpty, map } from 'lodash';

const ItemDeliveryLogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [itemDeliveries, setItemDeliveries] = useState([]);
  const [initialValues, setInitialValues] = useState({
    delivery_id: '',
    action: '',
    timestamp: '',
    details: '',
    photo: '',
  });

  useEffect(() => {
    const fetchItemDeliveryLog = async () => {
      if (id) {
        const response = await itemDeliveryLogService.getItemDeliveryLog(id);
        setInitialValues(response.data);
      }
    };
    fetchItemDeliveryLog();
  }, [id]);

  const validationSchema = Yup.object().shape({
    delivery_id: Yup.string().required('Delivery ID is required'),
    action: Yup.string().required('Action is required'),
    timestamp: Yup.string().required('Timestamp is required'),
    details: Yup.string().required('Details are required'),
    photo: Yup.string()
      .url('Must be a valid URL')
      .required('Photo URL is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (id) {
        await itemDeliveryLogService.updateItemDeliveryLog(id, values);
      } else {
        await itemDeliveryLogService.createItemDeliveryLog(values);
      }
      navigate('/item-delivery-logs');
    } catch (error) {
      console.error(error); // Handle error appropriately
    } finally {
      setSubmitting(false);
    }
  };

  const fetchItemDeliveries = async () => {
    const response = await itemDeliveryService.getItemDeliveries(localStorage?.getItem('event_id'));
    setItemDeliveries(response.data);
  };

  useEffect(() => {
    fetchItemDeliveries();
  }, []);

  const labelStyle = {
    color: '#000',
    fontSize: '0.87rem',
    marginTop: 8,
  };

  return (
    <LayoutContainer>
      <AdminHeader
        title={id ? 'Edit Item Delivery Log' : 'Add Item Delivery Log'}
        backTo='/item-delivery-logs'
      />
      <div className='page-content-wrapper scrollable'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div style={labelStyle}>Delivery</div>
                <Select
                  name='delivery_id'
                  value={values.delivery_id}
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {!isEmpty(itemDeliveries) ? (
                    map(itemDeliveries, (option) => (
                      <MenuItem key={option?.id} value={option?.id}>
                        {option?.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem>No data found</MenuItem>
                  )}
                </Select>
                {touched.delivery_id && errors.delivery_id && (
                  <FormHelperText style={{ color: '#D32F2F' }}>
                    {errors.delivery_id}
                  </FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div style={labelStyle}>Action</div>
                <TextField
                  name='action'
                  fullWidth
                  value={values.action}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.action && errors.action && (
                  <FormHelperText style={{ color: '#D32F2F' }}>
                    {errors.action}
                  </FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div style={labelStyle}>Timestamp</div>
                <TextField
                  name='timestamp'
                  fullWidth
                  type='datetime-local'
                  value={values.timestamp}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {touched.timestamp && errors.timestamp && (
                  <FormHelperText style={{ color: '#D32F2F' }}>
                    {errors.timestamp}
                  </FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div style={labelStyle}>Details</div>
                <TextField
                  name='details'
                  fullWidth
                  value={values.details}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.details && errors.details && (
                  <FormHelperText style={{ color: '#D32F2F' }}>
                    {errors.details}
                  </FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <div style={labelStyle}>Photo URL</div>
                <TextField
                  name='photo'
                  fullWidth
                  value={values.photo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.photo && errors.photo && (
                  <FormHelperText style={{ color: '#D32F2F' }}>
                    {errors.photo}
                  </FormHelperText>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type='submit'
                  variant='contained'
                  sx={{ backgroundColor: '#be210b', textTransform: 'none' }}
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </div>
    </LayoutContainer>
  );
};

export default ItemDeliveryLogForm;
